/**
 * themeCache - Serviço de cache para preferência de tema
 * 
 * Funcionalidades:
 * - Salva e recupera preferência de tema no localStorage
 * - Tratamento robusto de erros (localStorage desabilitado, quota excedida)
 * - Validação de dados
 * - Fallback para preferência do sistema
 */

const THEME_CACHE_KEY = 'wfmed_theme_preference'

/**
 * Valida se o tema é válido
 */
const isValidTheme = (theme) => {
  return theme === 'light' || theme === 'dark'
}

/**
 * Verifica se o localStorage está disponível
 */
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Salva a preferência de tema no cache
 * @param {string} theme - 'light' ou 'dark'
 * @returns {boolean} - true se salvou com sucesso, false caso contrário
 */
export const saveThemePreference = (theme) => {
  if (!isValidTheme(theme)) {
    console.warn(`[themeCache] Tema inválido: ${theme}. Deve ser 'light' ou 'dark'`)
    return false
  }

  if (!isLocalStorageAvailable()) {
    console.warn('[themeCache] localStorage não está disponível')
    return false
  }

  try {
    const cacheData = {
      theme,
      timestamp: Date.now(),
      version: '1.0.0' // Para futuras migrações
    }
    
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(cacheData))
    return true
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn('[themeCache] localStorage está cheio. Tentando limpar cache antigo...')
      // Tenta limpar apenas o cache antigo de tema (se houver versão antiga)
      try {
        localStorage.removeItem('theme') // Remove versão antiga se existir
        localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
          theme,
          timestamp: Date.now(),
          version: '1.0.0'
        }))
        return true
      } catch (retryError) {
        console.error('[themeCache] Erro ao salvar preferência de tema:', retryError)
        return false
      }
    }
    
    console.error('[themeCache] Erro ao salvar preferência de tema:', error)
    return false
  }
}

/**
 * Recupera a preferência de tema do cache
 * @returns {string|null} - 'light', 'dark' ou null se não houver preferência salva
 */
export const getThemePreference = () => {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    // Primeiro tenta a nova chave
    const cached = localStorage.getItem(THEME_CACHE_KEY)
    if (cached) {
      const cacheData = JSON.parse(cached)
      if (isValidTheme(cacheData.theme)) {
        return cacheData.theme
      }
    }

    // Fallback para chave antiga (compatibilidade)
    const oldTheme = localStorage.getItem('theme')
    if (isValidTheme(oldTheme)) {
      // Migra para a nova chave
      saveThemePreference(oldTheme)
      return oldTheme
    }

    return null
  } catch (error) {
    console.warn('[themeCache] Erro ao recuperar preferência de tema:', error)
    
    // Tenta recuperar da chave antiga como fallback
    try {
      const oldTheme = localStorage.getItem('theme')
      if (isValidTheme(oldTheme)) {
        return oldTheme
      }
    } catch {
      // Ignora erro no fallback
    }
    
    return null
  }
}

/**
 * Remove a preferência de tema do cache
 * @returns {boolean} - true se removeu com sucesso, false caso contrário
 */
export const clearThemePreference = () => {
  if (!isLocalStorageAvailable()) {
    return false
  }

  try {
    localStorage.removeItem(THEME_CACHE_KEY)
    // Remove também a chave antiga para limpeza completa
    localStorage.removeItem('theme')
    return true
  } catch (error) {
    console.warn('[themeCache] Erro ao limpar preferência de tema:', error)
    return false
  }
}

/**
 * Obtém a preferência do sistema (prefers-color-scheme)
 * @returns {string} - 'light' ou 'dark'
 */
export const getSystemThemePreference = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light'
  }

  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/**
 * Obtém o tema inicial (cache > sistema > padrão)
 * @returns {string} - 'light' ou 'dark'
 */
export const getInitialTheme = () => {
  // 1. Tenta recuperar do cache
  const cachedTheme = getThemePreference()
  if (cachedTheme) {
    return cachedTheme
  }

  // 2. Se não houver cache, usa preferência do sistema
  return getSystemThemePreference()
}
